import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { STAGES, stageMeta } from "../lib/stages";
import { StageBadge } from "../components/Stage";
import UsageWidget from "../components/UsageWidget";
import QrModal from "../components/QrModal";
import TodayFollowUp from "../components/TodayFollowUp";
import DailyBriefing from "../components/DailyBriefing";
import DashboardOverview from "../components/DashboardOverview";
import { useUsage } from "../lib/useUsage";
import { ArrowRight, Scan, IdentificationBadge, Sparkle, QrCode } from "@phosphor-icons/react";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [followups, setFollowups] = useState([]);
  const [qrOpen, setQrOpen] = useState(false);
  const { usage } = useUsage();
  const showSetupBanner = user && !user.onboarding_complete && !user.profile?.calendly_url;

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/followups", { params: { filter: "open" } }).then((r) => setFollowups(r.data.slice(0, 5)));
  }, []);

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="p-6 md:p-10" data-testid="dashboard-page">
      {showSetupBanner && (
        <div className="border p-5 mb-8 flex flex-wrap items-center justify-between gap-4" style={{ borderColor: "var(--accent)", background: "rgba(45,212,212,0.06)" }} data-testid="setup-banner">
          <div className="flex items-start gap-3">
            <Sparkle size={22} weight="duotone" color="var(--accent)" />
            <div>
              <div className="font-display font-bold text-lg">Finish setting up your card</div>
              <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>Add your Calendly link so every contact can book meetings with you automatically. 3 minutes.</p>
            </div>
          </div>
          <Link to="/setup" data-testid="dash-setup-btn" className="btn-primary flex items-center gap-2">Continue setup <ArrowRight weight="bold" /></Link>
        </div>
      )}
      <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="overline mb-2">// overview</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Operating room</h1>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={() => setQrOpen(true)}
            data-testid="dash-show-qr-btn"
            className="btn-primary flex items-center gap-2"
            style={{ background: "var(--accent)", color: "var(--bg)" }}
          >
            <QrCode weight="bold" /> Show my QR code
          </button>
          <Link to="/scan" data-testid="dash-scan-btn" className="btn-secondary flex items-center gap-2"><Scan weight="bold" /> Scan card</Link>
          <Link to="/my-card" data-testid="dash-mycard-btn" className="btn-secondary hidden md:flex items-center gap-2"><IdentificationBadge weight="bold" /> My card</Link>
        </div>
      </div>

      <QrModal open={qrOpen} onClose={() => setQrOpen(false)} user={user} />

      {/* Today's Follow-Up — only renders when there's something actionable */}
      <DailyBriefing />
      <DashboardOverview />
      <TodayFollowUp />

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-l border-t" style={{ borderColor: "var(--border)" }}>
        <StatCell label="Contacts" value={stats?.total_contacts ?? "—"} testid="stat-total" />
        <StatCell label="Overdue" value={stats?.overdue ?? "—"} accent={stats?.overdue > 0} testid="stat-overdue" />
        <StatCell label="Upcoming" value={stats?.upcoming ?? "—"} testid="stat-upcoming" />
        <StatCell label="Active" value={stats?.by_stage?.active ?? "—"} testid="stat-active" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-10">
        {/* Pipeline */}
        <section className="lg:col-span-7">
          <div className="overline mb-4">// pipeline</div>
          <div className="grid grid-cols-5 gap-0 border-l border-t" style={{ borderColor: "var(--border)" }}>
            {STAGES.map((s) => (
              <Link key={s.id} to={`/contacts?stage=${s.id}`} data-testid={`pipeline-${s.id}`}
                className="border-r border-b p-5 hover:bg-[#0F1E24]" style={{ borderColor: "var(--border)" }}>
                <span className="stage-dot" style={{ background: s.color }}></span>
                <div className="font-display font-black text-3xl mt-3">{stats?.by_stage?.[s.id] ?? 0}</div>
                <div className="overline mt-1">{s.label}</div>
              </Link>
            ))}
          </div>

          <div className="overline mt-10 mb-4">// recent contacts</div>
          <div className="border" style={{ borderColor: "var(--border)" }}>
            {(stats?.recent_contacts || []).length === 0 && (
              <div className="p-6 text-sm text-[#C7DDE5]">No contacts yet. Scan a card to get started.</div>
            )}
            {stats?.recent_contacts?.map((c, i) => (
              <Link key={c.id} to={`/contacts/${c.id}`} data-testid={`recent-contact-${i}`}
                className={`flex items-center justify-between p-4 hover:bg-[#15303A] ${i > 0 ? "border-t" : ""}`} style={{ borderColor: "var(--border)" }}>
                <div>
                  <div className="font-semibold">{c.name}</div>
                  <div className="overline mt-1">{c.company || "—"}{c.title ? ` · ${c.title}` : ""}</div>
                </div>
                <div className="flex items-center gap-3">
                  <StageBadge stage={c.stage} />
                  <ArrowRight weight="bold" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Follow-ups */}
        <section className="lg:col-span-5">
          <UsageWidget usage={usage} />

          <div className="flex items-center justify-between mb-4 mt-8">
            <div className="overline">// today &amp; soon</div>
            <Link to="/follow-ups" className="overline hover:text-white">all →</Link>
          </div>
          <div className="border" style={{ borderColor: "var(--border)" }}>
            {followups.length === 0 && (
              <div className="p-6 text-sm text-[#C7DDE5]">Nothing pending. You&apos;re caught up.</div>
            )}
            {followups.map((f, i) => {
              const overdue = f.due_date.slice(0, 10) < today;
              return (
                <div key={f.id} data-testid={`fup-${i}`} className={`p-4 ${i > 0 ? "border-t" : ""}`} style={{ borderColor: "var(--border)" }}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold">{f.contact?.name || "Contact"}</div>
                    <span className="tag" style={overdue ? { color: "var(--accent)", borderColor: "var(--accent)" } : {}}>
                      {new Date(f.due_date).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="text-sm text-[#C7DDE5] mt-2 line-clamp-2">{f.notes || f.type}</div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}

function StatCell({ label, value, accent, testid }) {
  return (
    <div className="border-r border-b p-6" style={{ borderColor: "var(--border)" }} data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="font-display font-black text-4xl mt-2" style={{ color: accent ? "var(--accent)" : "#fff" }}>
        {value}
      </div>
    </div>
  );
}
